import { Fragment, type ReactNode } from 'react';

/**
 * A render function for a tag. Receives the tag's inner content (its
 * "chunks") and returns the markup to wrap it in, e.g.
 * `(chunks) => <a href="...">{chunks}</a>`.
 */
export type TagRenderer = (chunks: ReactNode) => ReactNode;

export type Tags = Record<string, TagRenderer>;

/**
 * A run of plain text, an opening/closing delimiter for a known tag, or a
 * self-closing tag (`<name/>`) that renders with no inner content.
 */
type Token =
  | { type: 'text'; value: string }
  | { type: 'open'; name: string }
  | { type: 'close'; name: string }
  | { type: 'self'; name: string };

/** A node in the tree being built: the tag that opened it plus its children. */
interface Frame {
  /** The tag that opened this frame, or `null` for the root frame. */
  tag: string | null;
  children: ReactNode[];
}

/**
 * Splits `text` into text and tag tokens. Delimiters for tags that have no
 * renderer are dropped while the surrounding text is preserved, so unknown
 * tags simply vanish.
 */
function tokenize(
  text: string,
  isKnownTag: (name: string) => boolean,
): Token[] {
  const tagPattern = /<(\/?)([a-zA-Z][\w-]*)\s*(\/?)>/g;
  const tokens: Token[] = [];
  let lastIndex = 0;

  const pushText = (value: string) => {
    if (value) {
      tokens.push({ type: 'text', value });
    }
  };

  for (
    let match = tagPattern.exec(text);
    match !== null;
    match = tagPattern.exec(text)
  ) {
    const [full, slash, name, selfSlash] = match;
    if (name === undefined) {
      continue;
    }
    pushText(text.slice(lastIndex, match.index));
    lastIndex = match.index + full.length;

    if (isKnownTag(name)) {
      if (slash) {
        tokens.push({ type: 'close', name });
      } else if (selfSlash) {
        tokens.push({ type: 'self', name });
      } else {
        tokens.push({ type: 'open', name });
      }
    }
  }

  pushText(text.slice(lastIndex));
  return tokens;
}

/** The content a renderer receives: a lone child stays unwrapped, else the array. */
const chunksOf = (frame: Frame): ReactNode =>
  frame.children.length === 1 ? frame.children[0] : frame.children;

/**
 * Folds the flat token list into a React node tree, wrapping each matched
 * `<name>…</name>` pair with its renderer. Nesting is handled by a frame
 * stack; a stray closing tag is dropped, while an unclosed opening tag is
 * flushed back out as literal text along with its inner content.
 */
function buildTree(tokens: Token[], tags: Tags): ReactNode[] {
  const root: Frame = { tag: null, children: [] };
  const stack: Frame[] = [root];
  let key = 0;

  const current = (): Frame => stack[stack.length - 1] ?? root;

  const openTag = (name: string) => {
    stack.push({ tag: name, children: [] });
  };

  const closeTag = (name: string) => {
    const frame = current();
    if (stack.length <= 1 || frame.tag !== name) {
      return; // Stray closing tag — drop it.
    }
    const renderer = tags[name];
    if (!renderer) {
      return;
    }
    stack.pop();
    current().children.push(
      <Fragment key={key++}>{renderer(chunksOf(frame))}</Fragment>,
    );
  };

  const selfTag = (name: string) => {
    const renderer = tags[name];
    if (!renderer) {
      return;
    }
    // A self-closing tag has no inner content, so its renderer gets `undefined`.
    current().children.push(
      <Fragment key={key++}>{renderer(undefined)}</Fragment>,
    );
  };

  const flushUnclosedTags = () => {
    while (stack.length > 1) {
      const frame = stack.pop() ?? root;
      current().children.push(`<${frame.tag}>`, ...frame.children);
    }
  };

  for (const token of tokens) {
    if (token.type === 'text') {
      current().children.push(token.value);
    } else if (token.type === 'open') {
      openTag(token.name);
    } else if (token.type === 'self') {
      selfTag(token.name);
    } else {
      closeTag(token.name);
    }
  }

  flushUnclosedTags();
  return root.children;
}

/**
 * Parses `text` for the tags whose names are provided in `tags` and replaces
 * each `<name>…</name>` pair with the result of its render function, mirroring
 * how `react-intl`'s `FormattedMessage` handles rich-text tags. Tags whose name
 * is not present in `tags` are removed (their delimiters are stripped while the
 * inner content is kept). Nesting is supported. Unclosed known tags fall back
 * to literal text. Self-closing tags (`<name/>`) render with no inner content,
 * i.e. their renderer receives `undefined`.
 */
export function parseTags(text: string, tags: Tags): ReactNode[] {
  const isKnownTag = (name: string) =>
    Object.prototype.hasOwnProperty.call(tags, name);
  return buildTree(tokenize(text, isKnownTag), tags);
}
